import { motion } from 'framer-motion';
import { springPresets, staggerContainer, staggerItem } from '@/lib/motion';
import { CISP_RUN_LOG } from '@/data/index';
import { StatCard } from '@/components/Badges';
import { Activity, ShieldAlert, CheckCircle, AlertTriangle, TrendingUp } from 'lucide-react';
import { TierBadge, VerdictBadge, SeedBadge } from '@/components/Badges';

export default function Overview() {
  const total = CISP_RUN_LOG.length;
  const contaminated = CISP_RUN_LOG.filter((r) => r.seed_type === 'contaminated').length;
  const clean = CISP_RUN_LOG.filter((r) => r.seed_type === 'clean').length;
  const tier1 = CISP_RUN_LOG.filter((r) => r.tier === 'TIER-1').length;
  const tier2 = CISP_RUN_LOG.filter((r) => r.tier === 'TIER-2').length;
  const tier3 = CISP_RUN_LOG.filter((r) => r.tier === 'TIER-3').length;
  const retroEntered = CISP_RUN_LOG.filter((r) => r.retro_entered).length;

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      {/* Page Header */}
      <motion.div variants={staggerItem}>
        <div className="flex items-center gap-3 mb-1">
          <span className="text-xs font-mono text-primary uppercase tracking-widest">
            GG-CSAP PIPELINE
          </span>
          <span className="px-2 py-0.5 text-[10px] font-mono bg-primary/10 text-primary border border-primary/20 rounded">
            APRIL ASSESSMENT
          </span>
        </div>
        <h1 className="text-2xl font-bold text-foreground">System Overview</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          April Model Assessment runs. GG-CSAP v1.0 codebook applied. All 4 sessions retro-entered
          into CISP Run Log with tier assignments.
        </p>
      </motion.div>

      {/* Stats Row */}
      <motion.div variants={staggerItem} className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Runs" value={total} sub="April Assessment" />
        <StatCard
          label="Retro-Entered"
          value={`${retroEntered}/${total}`}
          sub="CISP Log entries"
          accent="text-primary"
        />
        <StatCard
          label="Contaminated Seeds"
          value={contaminated}
          sub={`${clean} clean seed`}
          accent="text-amber-400"
        />
        <StatCard
          label="Tier-3 Escalations"
          value={tier3}
          sub="Require analyst review"
          accent="text-red-400"
        />
      </motion.div>

      {/* Tier Distribution */}
      <motion.div variants={staggerItem} className="grid grid-cols-3 gap-4">
        {[
          { tier: 'TIER-1', count: tier1, label: 'Baseline Clean', color: 'text-emerald-400', bg: 'bg-emerald-950/30 border-emerald-800/30' },
          { tier: 'TIER-2', count: tier2, label: 'Contaminated / Cooperative', color: 'text-amber-400', bg: 'bg-amber-950/30 border-amber-800/30' },
          { tier: 'TIER-3', count: tier3, label: 'Anomalous / Escalated', color: 'text-red-400', bg: 'bg-red-950/30 border-red-800/30' },
        ].map(({ tier, count, label, color, bg }) => (
          <div key={tier} className={`rounded-lg border p-4 ${bg}`}>
            <div className="text-xs font-mono text-muted-foreground mb-1">{tier}</div>
            <div className={`text-3xl font-mono font-bold ${color}`}>{count}</div>
            <div className="text-xs text-muted-foreground mt-1">{label}</div>
          </div>
        ))}
      </motion.div>

      {/* Session Quick-View */}
      <motion.div variants={staggerItem}>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest">
            April Sessions — Quick View
          </span>
          <span className="h-px flex-1 bg-border" />
        </div>
        <div className="space-y-2">
          {CISP_RUN_LOG.map((run) => (
            <motion.div
              key={run.run_id}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={springPresets.gentle}
              className="flex items-center gap-4 px-4 py-3 rounded-lg bg-card border border-border hover:border-primary/30 transition-colors duration-150"
            >
              <span className="font-mono text-xs text-muted-foreground w-36 shrink-0">
                {run.run_id}
              </span>
              <span className="font-medium text-sm text-foreground flex-1 truncate">
                {run.session_name}
              </span>
              <SeedBadge seedType={run.seed_type} size="sm" />
              <VerdictBadge verdict={run.absolute_verdict} size="sm" />
              <TierBadge tier={run.tier} size="sm" />
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Pipeline Status */}
      <motion.div variants={staggerItem}>
        <div className="rounded-lg border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-foreground">Pipeline Status</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs font-mono">
            {[
              { label: 'Codebook Version', value: 'April 2026', icon: CheckCircle, color: 'text-emerald-400' },
              { label: 'Pipeline Version', value: 'GG-CSAP v1.0', icon: Activity, color: 'text-primary' },
              { label: 'Seed Types Active', value: 'CLEAN / CONTAMINATED', icon: ShieldAlert, color: 'text-amber-400' },
              { label: 'Pending Escalations', value: '1 (APR-004)', icon: AlertTriangle, color: 'text-red-400' },
            ].map(({ label, value, icon: Icon, color }) => (
              <div key={label} className="flex flex-col gap-1">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Icon className={`w-3 h-3 ${color}`} />
                  {label}
                </div>
                <div className={`${color} font-semibold`}>{value}</div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
